// Perfiles y usuarios del taller — Taller → Configuración → Perfiles y usuarios (964).
// Plantilla de perfiles recomendada (recepción y caja, técnico, jefe de taller,
// dueño) aplicada a las opciones del menú Taller de la empresa, y el rol, la caja
// y el técnico de cada usuario en un solo lugar: sin opciones del menú POS.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, Save, ShieldCheck, UserCog, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { fieldCls, tallerRpc, QK, useDialogoEnLinea } from './shared';

type Rol = 'recepcion' | 'tecnico' | 'jefe' | 'dueno';
interface Estado {
  nivel: number;
  roles: { rol: Exclude<Rol, 'dueno'>; perfil: string; nivel: number; perfil_id: string | null; rutas: string[]; aplicada: boolean }[];
  opciones: { ruta: string; nombre: string; submenu: string }[];
  usuarios: { id: string; username: string; perfil_id: string | null; perfil: string | null; nivel: number | null; yo: boolean; cajero: boolean; tecnico_id: string | null }[];
  tecnicos: { id: string; nombre: string; usuario_id: string | null }[];
  administrador_id: string | null;
}
const ROL_TXT: Record<Rol, string> = { recepcion: 'Recepción y caja', tecnico: 'Técnico', jefe: 'Jefe de taller', dueno: 'Dueño (administrador)' };
const ROL_DESC: Record<Rol, string> = {
  recepcion: 'Recibe equipos, cobra anticipos, entrega y factura, abre y cierra el turno de caja.',
  tecnico: 'Trabaja su cola en Mi banco y consulta las órdenes.',
  jefe: 'Todo lo de recepción, más catálogo, dictamen de garantía, Mi banco y compra de usados.',
  dueno: 'Todo el módulo: configuración, informes, comisiones, pasarela y perfiles.',
};

export function PerfilesTaller() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: e, isLoading } = useQuery<Estado>({ queryKey: [QK, 'perfiles'], queryFn: () => tallerRpc('perfiles_estado') });
  const inv = () => void qc.invalidateQueries({ queryKey: [QK] });
  const aplicar = useMutation({
    mutationFn: () => tallerRpc<{ habilitadas: number; quitadas: number }>('perfiles_aplicar'),
    onSuccess: (r) => { toast.success(`Perfiles aplicados: ${r.habilitadas} opción(es) habilitada(s), ${r.quitadas} quitada(s)`); inv(); },
    onError: (x) => toast.error(getErrorMessage(x)),
  });

  if (isLoading || !e) return <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>;
  const admin = e.nivel >= 5;
  const todasAplicadas = e.roles.every((r) => r.aplicada);
  const rolDe = (perfilId: string | null): Rol | '' => {
    if (!perfilId) return '';
    if (perfilId === e.administrador_id) return 'dueno';
    return e.roles.find((r) => r.perfil_id === perfilId)?.rol ?? '';
  };

  return (
    <div className="max-w-5xl space-y-4 p-4 sm:p-6">
      {dlg.ui}
      <div>
        <h1 className="flex items-center gap-2 text-xl font-semibold"><UserCog className="h-5 w-5" /> Perfiles y usuarios del taller</h1>
        <p className="text-sm text-muted-foreground">Con estos perfiles un usuario del taller trabaja solo con el menú Taller: no necesita opciones del POS. Lo administra un administrador (nivel 5).</p>
      </div>

      {/* Plantilla */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="h-5 w-5 text-primary" /> Perfiles recomendados
            {todasAplicadas ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">aplicados en esta empresa</span>
              : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">sin aplicar</span>}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Opción</th>
                {e.roles.map((r) => <th key={r.rol} className="py-1 pr-2 text-center">{ROL_TXT[r.rol]}<div className="font-normal">nivel {r.nivel}</div></th>)}
                <th className="py-1 text-center">{ROL_TXT.dueno}<div className="font-normal">nivel 5</div></th>
              </tr></thead>
              <tbody>{e.opciones.map((o, i) => (
                <tr key={o.ruta} className="border-b last:border-0">
                  <td className="py-1 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-1 pr-2">{o.nombre} <span className="text-xs text-muted-foreground">· {o.submenu}</span></td>
                  {e.roles.map((r) => <td key={r.rol} className="py-1 pr-2 text-center">{r.rutas.includes(o.ruta) ? <CheckCircle2 className="mx-auto h-4 w-4 text-emerald-600" /> : <span className="text-muted-foreground">—</span>}</td>)}
                  <td className="py-1 text-center">{o.ruta.startsWith('/taller/') ? <CheckCircle2 className="mx-auto h-4 w-4 text-emerald-600" /> : <span className="text-muted-foreground">—</span>}</td>
                </tr>))}
              </tbody>
              <tfoot><tr className="border-t-2 bg-muted/30 text-xs font-semibold"><td colSpan={2} className="py-2 pl-1">Opciones por perfil</td>
                {e.roles.map((r) => <td key={r.rol} className="py-2 text-center">{r.rutas.length} {r.aplicada ? <CheckCircle2 className="inline h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="inline h-3.5 w-3.5 text-amber-600" />}</td>)}
                <td className="py-2 text-center">{e.opciones.filter((o) => o.ruta.startsWith('/taller/')).length}</td></tr></tfoot>
            </table>
          </div>
          <ul className="space-y-0.5 text-xs text-muted-foreground">{(Object.keys(ROL_TXT) as Rol[]).map((r) => <li key={r}><strong>{ROL_TXT[r]}:</strong> {ROL_DESC[r]}</li>)}</ul>
          <p className="text-xs text-muted-foreground">El jefe de taller recibe además «Liquidación de compra» (Financiero), que exige la compra de usados. Aplicar solo toca las opciones del menú Taller de esta empresa; los demás módulos no cambian.</p>
          {admin && <div className="flex justify-end"><Button size="sm" isLoading={aplicar.isPending} onClick={async () => {
            if (await dlg.confirmar('¿Aplicar los perfiles del taller en esta empresa?', { detalle: 'Se habilitan las opciones de cada perfil y se quitan las opciones del menú Taller que no le corresponden.', aceptar: 'Aplicar' })) aplicar.mutate();
          }}><ShieldCheck className="mr-1 h-4 w-4" /> {todasAplicadas ? 'Volver a aplicar' : 'Aplicar perfiles'}</Button></div>}
        </CardContent>
      </Card>

      {/* Usuarios */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><UserCog className="h-5 w-5 text-primary" /> Usuarios de la empresa</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pl-3 pr-2">N°</th><th className="py-2 pr-2">Usuario</th><th className="py-2 pr-2">Perfil actual</th><th className="py-2 pr-2">Rol en el taller</th>
              <th className="py-2 pr-2">Cajero</th><th className="py-2 pr-2">Técnico (Mi banco)</th><th /></tr></thead>
            <tbody>{e.usuarios.map((u, i) => <FilaUsuario key={u.id} n={i + 1} u={u} e={e} rolActual={rolDe(u.perfil_id)} admin={admin} />)}</tbody>
            <tfoot><tr className="border-t-2 bg-muted/30 font-semibold"><td colSpan={7} className="py-2 pl-3">{e.usuarios.length} usuario(s) · {e.usuarios.filter((u) => u.cajero).length} cajero(s)</td></tr></tfoot>
          </table>
        </CardContent>
      </Card>
      <p className="text-xs text-muted-foreground">Crear usuarios nuevos o cambiar claves se hace en Sistemas → Usuarios. Cambiar el rol reemplaza el perfil del usuario en todo el ERP: si además usa otros módulos (contabilidad, nómina…), revise sus opciones.</p>
    </div>
  );
}

function FilaUsuario({ n, u, e, rolActual, admin }: { n: number; u: Estado['usuarios'][number]; e: Estado; rolActual: Rol | ''; admin: boolean }) {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [rol, setRol] = useState<Rol | ''>(rolActual);
  const [cajero, setCajero] = useState(u.cajero);
  const [tecnico, setTecnico] = useState(u.tecnico_id ?? '');
  useEffect(() => { setRol(rolActual); setCajero(u.cajero); setTecnico(u.tecnico_id ?? ''); }, [rolActual, u.cajero, u.tecnico_id]);
  const cambio = rol !== rolActual || cajero !== u.cajero || tecnico !== (u.tecnico_id ?? '');
  const guardar = useMutation({
    mutationFn: () => tallerRpc('usuario_rol', { _usuario: u.id, _rol: rol !== rolActual && rol ? rol : null, _cajero: cajero, _tecnico: tecnico || null }),
    onSuccess: () => { toast.success(`${u.username} actualizado`); void qc.invalidateQueries({ queryKey: [QK] }); },
    onError: (x) => toast.error(getErrorMessage(x)),
  });
  const onGuardar = async () => {
    if (rol && rol !== rolActual && !rolActual && u.perfil) {
      const ok = await dlg.confirmar(`¿Cambiar el perfil de ${u.username}?`, { detalle: `Hoy tiene «${u.perfil}». Pasará a «${ROL_TXT[rol]}» en todo el ERP; si usa otros módulos revise sus opciones.`, aceptar: 'Cambiar perfil' });
      if (!ok) return;
    }
    guardar.mutate();
  };
  return (
    <>
      {dlg.ui && <tr><td colSpan={7} className="p-2">{dlg.ui}</td></tr>}
      <tr className="border-b last:border-0">
        <td className="py-1.5 pl-3 pr-2 text-muted-foreground">{n}</td>
        <td className="py-1.5 pr-2 font-medium">{u.username}{u.yo && <span className="ml-1 text-xs text-muted-foreground">(usted)</span>}</td>
        <td className="py-1.5 pr-2 text-xs">{u.perfil ?? '—'} {u.nivel != null && <span className="text-muted-foreground">· nivel {u.nivel}</span>}</td>
        <td className="py-1.5 pr-2">
          <select className={`${fieldCls} h-8 w-48`} disabled={!admin || u.yo} value={rol} onChange={(x) => setRol(x.target.value as Rol | '')}>
            <option value="">{rolActual ? '' : '— Otro perfil (sin cambio) —'}</option>
            {(Object.keys(ROL_TXT) as Rol[]).map((r) => <option key={r} value={r}>{ROL_TXT[r]}</option>)}
          </select>
        </td>
        <td className="py-1.5 pr-2"><input type="checkbox" disabled={!admin} checked={cajero} onChange={(x) => setCajero(x.target.checked)} /></td>
        <td className="py-1.5 pr-2">
          <select className={`${fieldCls} h-8 w-44`} disabled={!admin} value={tecnico} onChange={(x) => setTecnico(x.target.value)}>
            <option value="">— Ninguno —</option>
            {e.tecnicos.filter((t) => !t.usuario_id || t.usuario_id === u.id).map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </select>
        </td>
        <td className="py-1.5 pr-3 text-right">{admin && cambio && <Button size="sm" isLoading={guardar.isPending} onClick={() => void onGuardar()}><Save className="mr-1 h-3.5 w-3.5" /> Guardar</Button>}</td>
      </tr>
    </>
  );
}
